import React, { useState, useRef, useEffect } from 'react';
import { Calendar, MapPin, ArrowLeftRight, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Logo from '../Assets/slider-bg.png';
import { FaAward, FaCheckCircle, FaShieldAlt, FaStar } from 'react-icons/fa';
import { IoCheckmarkCircle } from 'react-icons/io5';

interface TrustBadgeProps {
  icon: React.ReactNode;
  text: string;
  subtext?: string;
  highlight?: boolean;
}


const TrustBadge = ({ icon, text, subtext, highlight }: TrustBadgeProps) => (
  <div className={`flex items-start gap-3 p-4 rounded-xl transition-all duration-300 `}>
    <div className={`flex-shrink-0 ${highlight ? 'text-green-600' : 'text-green-600'}`}>
      {icon}
    </div>
    <div>
      <p className={`font-semibold ${highlight ? 'text-purple-100' : 'text-purple-100'}`}>
        {text}
      </p>
      {/* {subtext && (
        <p className="text-sm text-gray-100 mt-1">
          {subtext}
        </p>
      )} */}
    </div>
  </div>
);

const ModernTravelsHero = () => {
  const [tripType, setTripType] = useState('singleTrip');
  const [formData, setFormData] = useState({
    fromCity: '',
    toCity: '',
    pickupDate: '',
    returnDate: '',
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [activeDateField, setActiveDateField] = useState(null);
  
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const navigate = useNavigate();

  

  const badges = [
    {
      icon: <IoCheckmarkCircle className="w-6 h-6" />,
      text: "Cancel for free 24 hours before departure",
      subtext: "Flexible booking with full refund guarantee",
      highlight: true
    },
    {
      icon: <FaAward className="w-5 h-5" />,
      text: "Shree Puja Travelers' Choice 2026",
      subtext: "Award-winning service recognized globally"
    },
    {
      icon: <FaStar className="w-5 h-5" />,
      text: "Excellent 4.8/5",
      subtext: "Based on 10,000+ verified reviews"
    },
    {
      icon: <FaShieldAlt className="w-5 h-5" />,
      text: "Secure & Insured Rides",
      subtext: "Your safety is our top priority"
    }
  ]

  // Popular cities for suggestions
  const cities = [
    'Bangalore, Karnataka',
    'Chennai, Tamil Nadu',
    'Mysore, Karnataka',
    'Coorg, Karnataka',
    'Ooty, Tamil Nadu',
    'Pondicherry',
    'Tirupati, Andhra Pradesh',
    'Hyderabad, Telangana',
    'Goa',
    'Mumbai, Maharashtra',
  ];

  const [filteredFromSuggestions, setFilteredFromSuggestions] = useState(cities);
  const [filteredToSuggestions, setFilteredToSuggestions] = useState(cities);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowFromSuggestions(false);
        setShowToSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate next 60 days for date selection
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 60; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const dates = generateDates();

  const formatDate = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return {
      day: days[date.getDay()],
      date: date.getDate(),
      month: months[date.getMonth()],
      year: date.getFullYear(),
      full: `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`
    };
  };

  const handleDateSelect = (date) => {
    const formatted = formatDate(date);
    if (activeDateField === 'pickup') {
      setFormData({ ...formData, pickupDate: formatted.full });
    } else if (activeDateField === 'return') {
      setFormData({ ...formData, returnDate: formatted.full });
    }
    setShowDatePicker(false);
    setActiveDateField(null);
  };

  const handleFromCityChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, fromCity: value });
    
    if (value.trim() === '') {
      setFilteredFromSuggestions(cities);
    } else {
      const filtered = cities.filter(city => 
        city.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredFromSuggestions(filtered);
    }
    setShowFromSuggestions(true);
  };

  const handleToCityChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, toCity: value });
    
    if (value.trim() === '') {
      setFilteredToSuggestions(cities);
    } else {
      const filtered = cities.filter(city => 
        city.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredToSuggestions(filtered);
    }
    setShowToSuggestions(true);
  };

  const handleSuggestionSelect = (city, type) => {
    if (type === 'from') {
      setFormData({ ...formData, fromCity: city });
      setShowFromSuggestions(false);
    } else {
      setFormData({ ...formData, toCity: city });
      setShowToSuggestions(false);
    }
  };

  const handleExplore = () => {
    if (!formData.fromCity.trim() || !formData.toCity.trim() || !formData.pickupDate) {
      alert('Please fill in all required fields');
      return;
    }

    if (tripType === 'roundTrip' && !formData.returnDate) {
      alert('Please select return date for round trip');
      return;
    }

    // Navigate to vehicle selection with booking data
    const bookingData = {
      from: formData.fromCity,
      to: formData.toCity,
      pickupDate: formData.pickupDate,
      returnDate: formData.returnDate,
      tripType: tripType,
    };
    
    // Navigate to vehicle selection page
    navigate('/vehicle-selection', { state: bookingData });
  };

  const swapCities = () => {
    setFormData({
      ...formData,
      fromCity: formData.toCity,
      toCity: formData.fromCity,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 via-orange-50 to-yellow-100 relative overflow-hidden" style={{ background: 'rgba(65, 1, 150, 1)' }}>
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(https://t3.ftcdn.net/jpg/12/78/75/42/360_F_1278754237_rMGKqTmKIPIUYZh04kmIIM3P1OCKqOXi.jpg)`,
          }}
        />
      </div>

      <div className="absolute top-40 right-20 w-32 h-32 bg-orange-400 rounded-full shadow-2xl animate-pulse hidden lg:block"></div>

      <main className="max-w-7xl mx-auto px-4 py-8 lg:py-16 relative z-10">
        <div className="text-center mb-12">
        <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
        <span className="block">Journey from the</span>
        <span className="relative inline-block">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-100 via-gray-100 to-gray-100" >
            City of Pearls
          </span>
          <svg className="absolute -bottom-2 left-0 w-full h-2 text-orange-400" viewBox="0 0 200 10">
            <path d="M0,5 Q50,0 100,5 T200,5" stroke="currentColor" strokeWidth="2" fill="none"/>
          </svg>
        </span>
        <span className="block mt-4">To Your Dream Destination</span>
      </h1>
      
        </div>

        <div className="max-w-4xl mx-auto" ref={suggestionsRef}>
          <div className="flex items-center justify-between mb-6">
<h3 className="text-2xl font-bold text-gray-900 animate-[pulse_2s_ease-in-out_infinite] hover:animate-none hover:text-gray-900 transition-all cursor-pointer" >
  For Bookings
</h3>          
            <div className="flex gap-2 bg-gray-400 rounded-full p-1 shadow-lg">
              <button
                onClick={() => setTripType('singleTrip')}
                className={`px-6 py-2 rounded-full font-semibold transition ${
                  tripType === 'singleTrip'
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-900 hover:bg-gray-100'
                }`}
              >
                Single Trip
              </button>
              <button
                onClick={() => setTripType('roundTrip')}
                className={`px-6 py-2 rounded-full font-semibold transition ${
                  tripType === 'roundTrip'
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Round Trip
              </button>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-6 lg:p-8 relative">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
              {/* From City with manual input */}
              <div className="lg:col-span-3 relative">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  From City
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-900" size={20} />
                  <input
                    type="text"
                    ref={fromInputRef}
                    value={formData.fromCity}
                    onChange={handleFromCityChange}
                    onFocus={() => setShowFromSuggestions(true)}
                    placeholder="Type or select from city"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-900 focus:outline-none text-gray-700"
                  />
                </div>
                
                {showFromSuggestions && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredFromSuggestions.map((city, index) => (
                      <div
                        key={index}
                        onClick={() => handleSuggestionSelect(city, 'from')}
                        className="px-4 py-3 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-center gap-2">
                          <MapPin size={16} className="text-purple-900" />
                          <span className="text-gray-700">{city}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="lg:col-span-1 flex justify-center">
                <button
                  onClick={swapCities}
                  className="p-3 bg-purple-100 hover:bg-purple-200 rounded-full transition"
                  title="Swap cities"
                >
                  <ArrowLeftRight className="text-gray-900" size={20} />
                </button>
              </div>

              {/* To City with manual input */}
              <div className="lg:col-span-3 relative">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  To City
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-900" size={20} />
                  <input
                    type="text"
                    ref={toInputRef}
                    value={formData.toCity}
                    onChange={handleToCityChange}
                    onFocus={() => setShowToSuggestions(true)}
                    placeholder="Type or select to city"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-gray-900 focus:outline-none text-gray-700"
                  />
                </div>
                
                {showToSuggestions && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredToSuggestions.map((city, index) => (
                      <div
                        key={index}
                        onClick={() => handleSuggestionSelect(city, 'to')}
                        className="px-4 py-3 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-center gap-2">
                          <MapPin size={16} className="text-purple-900" />
                          <span className="text-gray-700">{city}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="lg:col-span-2">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Pickup Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-900" size={20} />
                  <input
                    type="text"
                    value={formData.pickupDate}
                    onClick={() => {
                      setActiveDateField('pickup');
                      setShowDatePicker(true);
                    }}
                    placeholder="Fri, 2 Jan"
                    readOnly
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-900 focus:outline-none cursor-pointer text-gray-700"
                  />
                </div>
              </div>

              {tripType === 'roundTrip' && (
                <div className="lg:col-span-2">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Return Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-900" size={20} />
                    <input
                      type="text"
                      value={formData.returnDate}
                      onClick={() => {
                        setActiveDateField('return');
                        setShowDatePicker(true);
                      }}
                      placeholder="Return Date"
                      readOnly
                      className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-900 focus:outline-none cursor-pointer text-gray-700"
                    />
                  </div>
                </div>
              )}

              <div className={tripType === 'roundTrip' ? 'lg:col-span-1' : 'lg:col-span-3'}>
                <button
                  onClick={handleExplore}
                  className="w-full bg-purple-900 hover:bg-purple-800 text-white font-bold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2 shadow-lg"
                  style={{ backgroundColor: 'rgba(65, 1, 150, 1)' }}
                >
                  <Search size={20} />
                  EXPLORE
                </button>
              </div>
            </div>

            {showDatePicker && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
                  <div className="bg-purple-900 text-white p-4 flex justify-between items-center">
                    <h3 className="text-lg font-bold">
                      Select {activeDateField === 'pickup' ? 'Pickup' : 'Return'} Date
                    </h3>
                    <button
                      onClick={() => {
                        setShowDatePicker(false);
                        setActiveDateField(null);
                      }}
                      className="text-white hover:text-gray-200"
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="p-4 overflow-y-auto max-h-[60vh]">
                    <div className="grid grid-cols-3 gap-3">
                      {dates.map((date, index) => {
                        const formatted = formatDate(date);
                        return (
                          <button
                            key={index}
                            onClick={() => handleDateSelect(date)}
                            className="flex flex-col items-center p-3 border-2 border-gray-200 rounded-lg hover:border-purple-900 hover:bg-purple-50 transition"
                          >
                            <span className="text-xs text-gray-500 font-semibold">{formatted.day}</span>
                            <span className="text-2xl font-bold text-purple-900">{formatted.date}</span>
                            <span className="text-xs text-gray-600">{formatted.month}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

       <section className="py-4 md:py-12 relative z-10">
  <div className="container mx-auto px-3 md:px-6">
    <div className="max-w-6xl mx-auto">
      {/* Mobile: Single row, very compact */}
      <div className="md:hidden">
        <div className="flex flex-nowrap justify-between items-center gap-1">
          {badges.map((badge, index) => (
            <div
              key={index}
              className="flex-1 min-w-0"
            >
              <div className="flex flex-col items-center text-center p-1.5 bg-white/95 rounded border border-gray-100">
                <div className={`mb-1 ${badge.highlight ? 'text-green-600' : 'text-green-600'}`}>
                  {React.cloneElement(badge.icon, { className: 'w-3 h-3' })}
                </div>
                <p className="text-[8px] font-semibold text-gray-900 leading-tight truncate w-full">
                  {badge.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop stays the same */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {badges.map((badge, index) => (
          <TrustBadge
            key={index}
            icon={badge.icon}
            text={badge.text}
            subtext={badge.subtext}
            highlight={badge.highlight}
          />
        ))}
      </div>
    </div>
  </div>
</section>
      </main>

      <a
        href="https://wa.me/919100390049"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-2xl transition z-50"
      >
        <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default ModernTravelsHero;